import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { format } from 'date-fns';
import { X, Download } from 'lucide-react';

export const MetricItem = ({ label, value }) => (
  <Box sx={{
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'baseline',
    gap: '5px',
    minWidth: '80px'
  }}>
    <Typography sx={{
      color: '#666666',
      fontSize: '9px',
      fontFamily: "'Open Sans', sans-serif",
      fontWeight: 400,
      textTransform: 'uppercase',
      lineHeight: '10px',
      wordWrap: 'break-word'
    }}>
      {label}:
    </Typography>
    <Typography sx={{
      color: '#1F1F1F',
      fontSize: '9px',
      fontFamily: "'Open Sans', sans-serif",
      fontWeight: 600,
      wordWrap: 'break-word'
    }}>
      {value}
    </Typography>
  </Box>
);

export const IngredientsPanel = ({ selectedOrder, mostRecentOrder, recommendedOrder, onClose }) => {
  const exportFormulations = () => {
    const formulations = [];

    // Helper to format a single formulation
    const formatFormulation = (order, type) => {
      if (!order) return null;

      const lines = [];
      lines.push(`=== ${type.toUpperCase()} FORMULATION ===`);
      lines.push('');
      lines.push(`Process Order: ${order.processOrder}`);
      lines.push(`Date: ${format(new Date(order.orderDate), 'MMM d, yyyy')}`);
      lines.push(`Grade: ${order.grade || 'N/A'}`);
      lines.push('');
      lines.push('METRICS:');
      lines.push(`  Yield: ${order.metrics?.['yield']?.toFixed(2) || '0.00'}`);
      lines.push(`  Airflow: ${order.metrics?.['af_c_avg']?.toFixed(2) || '0.00'}`);
      lines.push(`  IFD: ${order.metrics?.['ifd_25_avg']?.toFixed(2) || '0.00'}`);
      lines.push(`  Density: ${order.metrics?.['density_avg']?.toFixed(2) || '0.00'}`);
      lines.push('');
      lines.push('INGREDIENTS (PPH):');

      // Extract and format ingredients
      const ingredientList = {};

      // Collect ingredient names
      Object.entries(order.ingredients || {}).forEach(([key, value]) => {
        if (key.startsWith('ingredient_')) {
          const channelNum = key.split('_')[1];
          if (!ingredientList[channelNum]) {
            ingredientList[channelNum] = { name: value };
          }
        }
      });

      // Add PPH values
      Object.entries(order.ingredients || {}).forEach(([key, value]) => {
        if (key.startsWith('pph_')) {
          const channelNum = key.split('_')[1];
          if (ingredientList[channelNum]) {
            ingredientList[channelNum].pph = value;
          }
        }
      });

      // Sort and format
      const sortedIngredients = Object.entries(ingredientList)
        .map(([channelNum, data]) => ({
          name: data.name,
          value: data.pph,
          channelNumber: parseInt(channelNum)
        }))
        .filter(ingredient => ingredient.value > 0)
        .sort((a, b) => a.channelNumber - b.channelNumber);

      if (sortedIngredients.length === 0) {
        lines.push('  No ingredients data available');
      } else {
        sortedIngredients.forEach(ingredient => {
          const pphValue = typeof ingredient.value === 'number' ? ingredient.value.toFixed(3) : '0.000';
          lines.push(`  ${ingredient.name} (Ch ${ingredient.channelNumber}): ${pphValue}`);
        });
      }

      return lines.join('\n');
    };

    // Add each formulation
    if (recommendedOrder) {
      const formatted = formatFormulation(recommendedOrder, 'Recommended');
      if (formatted) formulations.push(formatted);
    }

    if (mostRecentOrder) {
      const formatted = formatFormulation(mostRecentOrder, 'Most Recent');
      if (formatted) formulations.push(formatted);
    }

    if (selectedOrder) {
      const formatted = formatFormulation(selectedOrder, 'Selected');
      if (formatted) formulations.push(formatted);
    }

    // Create file content
    const fileContent = [
      'FORMULATION COMPARISON REPORT',
      `Generated: ${format(new Date(), 'MMM d, yyyy HH:mm:ss')}`,
      '',
      ''.padEnd(80, '='),
      '',
      formulations.join('\n\n' + ''.padEnd(80, '-') + '\n\n'),
      '',
      ''.padEnd(80, '='),
    ].join('\n');

    // Create and download file
    const blob = new Blob([fileContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `formulations_${format(new Date(), 'yyyyMMdd_HHmmss')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderIngredients = (ingredients) => {
    if (!ingredients || Object.keys(ingredients).length === 0) {
      return (
        <Box sx={{
          alignSelf: 'stretch',
          padding: '20px',
          textAlign: 'center',
          backgroundColor: 'rgba(0,0,0,0.02)',
          borderRadius: '4px'
        }}>
          <Typography sx={{
            color: '#999999',
            fontSize: '12px',
            fontFamily: "'Open Sans', sans-serif",
            fontWeight: 400,
            fontStyle: 'italic'
          }}>
            No ingredients data available
          </Typography>
        </Box>
      );
    }

    // Collect ingredient names
    const ingredientList = Object.entries(ingredients)
      .reduce((acc, [key, value]) => {
        if (key.startsWith('ingredient_')) {
          const channelNum = key.split('_')[1];
          if (!acc[channelNum]) {
            acc[channelNum] = { name: value };
          }
        }
        return acc;
      }, {});

    // Add PPH values from pph_N keys
    Object.entries(ingredients)
      .forEach(([key, value]) => {
        if (key.startsWith('pph_')) {
          const channelNum = key.split('_')[1];
          if (ingredientList[channelNum]) {
            ingredientList[channelNum].pph = value;
          }
        }
      });

    // Convert to array and sort by channel number
    const sortedIngredients = Object.entries(ingredientList)
      .map(([channelNum, data]) => ({
        name: data.name,
        value: data.pph,
        channelNumber: parseInt(channelNum)
      }))
      .filter(ingredient => ingredient.value > 0) // Only show ingredients with non-zero PPH values
      .sort((a, b) => a.channelNumber - b.channelNumber);

    if (sortedIngredients.length === 0) {
      return null;
    }

    return (
      <Box sx={{ 
        alignSelf: 'stretch',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '8px',
        display: 'flex'
      }}>
        {sortedIngredients.map((ingredient, index) => (
          <Box key={index} sx={{
            alignSelf: 'stretch',
            height: '32px',
            paddingLeft: '4px',
            paddingRight: '4px',
            paddingTop: '2px',
            paddingBottom: '2px',
            borderRadius: '4px',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            display: 'flex'
          }}>
            <Box sx={{
              alignSelf: 'stretch',
              height: '32px',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              display: 'flex'
            }}>
              <Typography sx={{
                color: '#1F1F1F',
                fontSize: '11px',
                fontFamily: "'Open Sans', sans-serif",
                fontWeight: 400,
                lineHeight: '13px',
                wordWrap: 'break-word'
              }}>
                {ingredient.name} ({ingredient.channelNumber})
              </Typography>
              <Box sx={{
                alignSelf: 'stretch',
                justifyContent: 'flex-start',
                alignItems: 'baseline',
                gap: '2px',
                display: 'inline-flex'
              }}>
                <Typography sx={{
                  color: '#1F1F1F',
                  fontSize: '15px',
                  fontFamily: "'Open Sans', sans-serif",
                  fontWeight: 400,
                  lineHeight: '19px',
                  wordWrap: 'break-word'
                }}>
                  {typeof ingredient.value === 'number' ? ingredient.value.toFixed(3) : '0.000'}
                </Typography>
                <Typography sx={{
                  color: '#1F1F1F',
                  fontSize: '9px',
                  fontFamily: "'Open Sans', sans-serif",
                  fontWeight: 400,
                  lineHeight: '13px',
                  wordWrap: 'break-word'
                }}>
                  PPH
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const RecipePanel = ({ order, title, showClose }) => (
    <Box sx={{ 
      flex: '1 1 0',
      padding: '5px',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      gap: '10px',
      display: 'inline-flex',
      position: 'relative'
    }}>
      {showClose && (
        <IconButton 
          onClick={onClose}
          size="small"
          sx={{
            padding: '2px',
            color: '#666666',
            position: 'absolute',
            right: '5px',
            top: '5px',
            '&:hover': {
              color: '#1F1F1F',
              backgroundColor: 'transparent'
            }
          }}
        >
          <X size={16} />
        </IconButton>
      )}
      <Box sx={{
        alignSelf: 'stretch',
        paddingBottom: '10px',
        borderBottom: '1px #E5E3E1 solid',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '10px',
        display: 'flex'
      }}>
        <Typography sx={{
          color: '#666666',
          fontSize: '9px',
          fontFamily: "'Open Sans', sans-serif",
          fontWeight: 700,
          textTransform: 'uppercase',
          lineHeight: '10px',
          wordWrap: 'break-word'
        }}>
          {title}
        </Typography>
        <Box sx={{
          alignSelf: 'stretch',
          paddingRight: '10px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          display: 'inline-flex'
        }}>
          <Box sx={{
            flex: '1 1 0',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            display: 'inline-flex'
          }}>
            <Typography sx={{
              color: '#666666',
              fontSize: '9px',
              fontFamily: "'Open Sans', sans-serif",
              fontWeight: 700,
              textTransform: 'uppercase',
              lineHeight: '10px',
              wordWrap: 'break-word'
            }}>
              Process Order
            </Typography>
            <Typography sx={{
              color: '#1F1F1F',
              fontSize: '17px',
              fontFamily: "'Open Sans', sans-serif",
              fontWeight: 600,
              wordWrap: 'break-word'
            }}>
              {order.processOrder}
            </Typography>
          </Box>
          <Box sx={{
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            display: 'inline-flex'
          }}>
            <Typography sx={{
              color: '#666666',
              fontSize: '9px',
              fontFamily: "'Open Sans', sans-serif",
              fontWeight: 700,
              textTransform: 'uppercase',
              lineHeight: '10px',
              wordWrap: 'break-word'
            }}>
              Yield
            </Typography>
            <Typography sx={{
              color: '#1F1F1F',
              fontSize: '17px',
              fontFamily: "'Open Sans', sans-serif",
              fontWeight: 600,
              wordWrap: 'break-word'
            }}>
              {order.metrics?.['yield']?.toFixed(2) || '0.00'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{
          alignSelf: 'stretch',
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gridTemplateRows: 'repeat(3, auto)',
          gap: '8px',
          alignItems: 'center'
        }}>
          <MetricItem label="Date" value={format(new Date(order.orderDate), 'MMM d yy')} />
          <MetricItem label="Grade" value={order.grade || 'N/A'} />
          <MetricItem label="Airflow" value={order.metrics?.['af_c_avg']?.toFixed(2) || '0.00'} />
          <MetricItem label="IFD" value={order.metrics?.['ifd_25_avg']?.toFixed(2) || '0.00'} />
          <MetricItem label="Density" value={order.metrics?.['density_avg']?.toFixed(2) || '0.00'} />
        </Box>
      </Box>

      {renderIngredients(order.ingredients)}
    </Box>
  );

  return (
    <Box sx={{
      alignSelf: 'stretch',
      paddingTop: '10px',
      paddingBottom: '5px',
      paddingLeft: '20px',
      paddingRight: '20px',
      background: '#302E2C',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      gap: '10px',
      display: 'inline-flex'
    }}>
      <Box sx={{
        alignSelf: 'stretch',
        paddingTop: '5px',
        paddingBottom: '10px',
        paddingLeft: '10px',
        paddingRight: '10px',
        background: '#FCFCFA',
        boxShadow: '0px 8px 12px rgba(0, 0, 0, 0.60)',
        borderRadius: '8px',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '5px',
        display: 'inline-flex',
        position: 'relative'
      }}>
        <IconButton
          onClick={exportFormulations}
          size="small"
          sx={{
            position: 'absolute',
            right: '10px',
            bottom: '10px',
            padding: '6px',
            color: '#666666',
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
            zIndex: 1,
            '&:hover': {
              color: '#1F1F1F',
              backgroundColor: 'rgba(0, 0, 0, 0.08)'
            }
          }}
          title="Save all formulations to file"
        >
          <Download size={16} />
        </IconButton>
        {recommendedOrder && (
          <>
            <RecipePanel order={recommendedOrder} title="Recommended" showClose={false} />
            <Box sx={{
              alignSelf: 'stretch',
              width: '1px',
              backgroundColor: '#D9D5D1'
            }} />
          </>
        )}
        {mostRecentOrder && (
          <>
            <RecipePanel order={mostRecentOrder} title="Most Recent" showClose={false} />
            <Box sx={{
              alignSelf: 'stretch',
              width: '1px',
              backgroundColor: '#D9D5D1'
            }} />
          </>
        )}
        <RecipePanel order={selectedOrder} title="Selected" showClose={true} />
      </Box>
    </Box>
  );
}; 